import React from 'react';
import { Autocomplete, Box, Divider, TextField, Typography, Tooltip, Skeleton } from '@mui/material';
import TableData from '../../custom/Table/TableData';
import CellType from '../../../constants/type';
import useI18n from '../../../hooks/useI18n';
import BannerCarousel from '../../custom/BannerCarousel/BannerCarousel';
import Search from '../../custom/Search/Search';
import priceListService from "../../../api/pricelist";
import bannersService from "../../../api/banners";
import useUserInfo from '../../../hooks/useUserInfo';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FormGroup from '@mui/material/FormGroup';
import useMediaQuery from '@mui/material/useMediaQuery';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';

import { basketState } from '../../../state/basketState';
import { updateBasketState } from '../../../state/updateBasketState';
import { useRecoilState } from "recoil";
import basketService from '../../../api/basket';
import DetailScreen from '../../custom/DetailScreen/DetailScreen';
import { compDetailUrl } from '../../../config';
import getEffectiveOrderQty from '../../../helpers/getEffectiveOrderQty';


function PriceList() {
    const config = require('../../../config');
    const { language: lang, l } = useI18n();

    const [priceList, setPriceList] = React.useState();
    const [priceListLoading, setPriceListLoading] = React.useState(false);

    const [searchValue, setSearchValue] = React.useState('');
    const [checkedOrdered, setCheckedOrdered] = React.useState(false);
    const [orderCategories, setOrderCategories] = React.useState([]);

    // Unified filters state
    const [filtersConfig, setFiltersConfig] = React.useState([]); // Filter definitions from server
    const [selectedFilters, setSelectedFilters] = React.useState({}); // Currently selected values { LEVEL1: [], COLOR: [], ... }
    const [filterCombinations, setFilterCombinations] = React.useState([]); // For dynamic options calculation
    const [filtersLoading, setFiltersLoading] = React.useState(true); // Loading state for filters

    // null — панель в состоянии по умолчанию для текущего брейкпоинта
    // (на десктопе открыта, на мобильном скрыта), пока пользователь её не переключил.
    const [openFilters, setOpenFilters] = React.useState(null);
    const isDesktop = useMediaQuery((theme) => theme.breakpoints.up('md'), { noSsr: true });
    const showFilters = openFilters ?? isDesktop;
    const toggleFilters = () => setOpenFilters(!showFilters);
    const [activeBanners, setActiveBanners] = React.useState([]);

    const { user } = useUserInfo();

    const [totals, setTotals] = React.useState({ totSum: 0, totWeight: 0, totVolume: 0, FRRATE: 0 });
    const [, setBasketRecoil] = useRecoilState(basketState);
    useRecoilState(updateBasketState);

    const [pagePagination, setPagePagination] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(100);
    const [ownPagination, setOwnPagination] = React.useState({
        page: pagePagination,
        pageSize: pageSize,
        totalItems: 1,
        rowsPerPageOptions: [20, 50, 100, 200],
    });
    const [openDetail, setOpenDetail] = React.useState(false);
    const [detailUrl, setDetailUrl] = React.useState('');

    const [isGrouped, setIsGrouped] = React.useState(true);

    // Ref to track if it's the initial load
    const isInitialLoad = React.useRef(true);

    // Helper function to get localized label
    const getLocalizedLabel = (labelObj) => {
        if (typeof labelObj === 'string') return labelObj;
        if (!labelObj) return '';
        const langKey = lang === 'ru' ? 'ru' : lang === 'en' ? 'en' : 'ua';
        return labelObj[langKey] || labelObj.ua || labelObj.en || '';
    };

    // Helper function to get option value
    const getOptionValue = (option) => {
        if (typeof option === 'string') return option;
        return option?.value || option?.Attribute || '';
    };

    // Helper function to get option label for display
    const getOptionLabel = (option, filter) => {
        if (typeof option === 'string') return option;
        if (option?.label) {
            return getLocalizedLabel(option.label);
        }
        return option?.value || option?.Attribute || '';
    };

    // Fetch price list data
    const getPriceList = async (prop) => {
        setPriceList(priceList ? [] : undefined);
        setPriceListLoading(true);

        try {
            if (user) {
                const result = await priceListService.findAllByUserId(prop, lang);

                setOwnPagination({
                    page: result.currentPage,
                    pageSize: pageSize,
                    totalItems: result.totalItems,
                    totalPages: result.totalPages,
                    rowsPerPageOptions: [20, 50, 100, 200],
                });

                if (result) {
                    setPriceList(result.data);
                }
            }
        } catch (error) {
            console.error('Error getPriceList :>> ', error);
        } finally {
            setPriceListLoading(false);
        }
    };

    // Fetch unified filters with dynamic options
    const fetchUnifiedFilters = React.useCallback(async (currentSelection = {}) => {
        try {
            setFiltersLoading(true);
            const result = await priceListService.getUnifiedFilters(currentSelection, lang);
            setFiltersConfig(result.filters || []);
            setFilterCombinations(result.filterCombinations || []);

            // Initialize selected filters state for new filter types
            const newSelectedFilters = { ...currentSelection };
            (result.filters || []).forEach(filter => {
                if (!(filter.key in newSelectedFilters)) {
                    newSelectedFilters[filter.key] = [];
                }
            });

            // Only update if there are new keys
            if (Object.keys(newSelectedFilters).length !== Object.keys(currentSelection).length) {
                setSelectedFilters(newSelectedFilters);
            }
        } catch (error) {
            console.error('Error fetchUnifiedFilters :>> ', error);
        } finally {
            setFiltersLoading(false);
        }
    }, [lang]);

    // Fetch active banners
    const fetchActiveBanners = React.useCallback(async () => {
        try {
            const banners = await bannersService.getActiveBanners(lang);
            setActiveBanners(Array.isArray(banners) ? banners : []);
        } catch (error) {
            console.info('Error fetching active banners:', error);
            // Якщо банери не знайдено, не показуємо помилку
            setActiveBanners([]);
        }
    }, [lang]);

    // Fetch order categories and totals
    const getOrderedByCategoryAndSumByCustCode = React.useCallback(async () => {
        try {
            const orderByCategoryCum = await priceListService.getOrderedByCategoryAndSumByCustCode(lang);
            setOrderCategories(orderByCategoryCum.dataOrderedByCategories || []);
            setTotals({
                totSum: orderByCategoryCum.totSum || 0,
                totWeight: orderByCategoryCum.totWeight || 0,
                totVolume: orderByCategoryCum.totVolume || 0,
                FRRATE: orderByCategoryCum.FRRATE || 0
            });
        } catch (error) {
            console.error('Error getOrderedByCategoryAndSumByCustCode :>> ', error);
        }
    }, [lang]);

    // Calculate available options for each filter based on current selection
    // This runs on the client side using filterCombinations data
    const calculateAvailableOptions = React.useCallback((filterKey, currentSelection) => {
        if (!filterCombinations.length) return null;

        const levelKeys = ['LEVEL1', 'LEVEL2', 'LEVEL3'];

        const availableOptions = new Set();

        filterCombinations.forEach(combo => {
            // Get combo values with proper language
            const comboLevels = {
                LEVEL1: lang === 'ru' ? combo.LEVEL1RUS : combo.LEVEL1,
                LEVEL2: lang === 'ru' ? combo.LEVEL2RUS : combo.LEVEL2,
                LEVEL3: lang === 'ru' ? combo.LEVEL3RUS : combo.LEVEL3
            };

            // Check if this combination matches all OTHER selected filters (not the current one)
            let matchesOtherFilters = true;

            for (const [key, values] of Object.entries(currentSelection)) {
                if (key === filterKey) continue; // Skip the filter we're calculating options for
                if (!values || values.length === 0) continue;

                const selectedValues = values.map(v => getOptionValue(v));

                if (levelKeys.includes(key)) {
                    if (!selectedValues.includes(comboLevels[key])) {
                        matchesOtherFilters = false;
                        break;
                    }
                } else {
                    // Attribute filter - key is TYPE, check attributes of that type
                    const comboAttrsForType = (combo.attributeTypes && combo.attributeTypes[key]) || [];
                    const hasMatchingAttr = comboAttrsForType.some(attr =>
                        selectedValues.includes(attr)
                    );
                    if (!hasMatchingAttr) {
                        matchesOtherFilters = false;
                        break;
                    }
                }
            }

            if (matchesOtherFilters) {
                if (levelKeys.includes(filterKey)) {
                    const value = comboLevels[filterKey];
                    if (value) availableOptions.add(value);
                } else {
                    // For attribute filters, add attributes from this combo that match the TYPE
                    const comboAttrsForType = (combo.attributeTypes && combo.attributeTypes[filterKey]) || [];
                    comboAttrsForType.forEach(attr => {
                        availableOptions.add(attr);
                    });
                }
            }
        });

        return availableOptions;
    }, [filterCombinations, lang]);

    // Get filtered options for a specific filter
    const getFilteredOptions = React.useCallback((filter) => {
        const options = filter.options || [];

        // If no combinations data or no selections, return all options
        const hasAnySelection = Object.values(selectedFilters).some(v => v && v.length > 0);
        if (!filterCombinations.length || !hasAnySelection) {
            // Filter out empty values for LEVEL3
            if (filter.key === 'LEVEL3') {
                return options.filter(opt => getOptionValue(opt) !== '');
            }
            return options;
        }

        const availableValues = calculateAvailableOptions(filter.key, selectedFilters);
        if (!availableValues) return options;

        let filteredOptions = options.filter(opt => {
            const value = getOptionValue(opt);
            return availableValues.has(value);
        });

        // Filter out empty values for LEVEL3
        if (filter.key === 'LEVEL3') {
            filteredOptions = filteredOptions.filter(opt => getOptionValue(opt) !== '');
        }

        return filteredOptions;
    }, [selectedFilters, filterCombinations, calculateAvailableOptions]);

    // Initial load effect
    React.useEffect(() => {
        if (user) {
            fetchUnifiedFilters({});
            getOrderedByCategoryAndSumByCustCode();
            fetchActiveBanners();
        }
    }, [user, lang, fetchUnifiedFilters, getOrderedByCategoryAndSumByCustCode, fetchActiveBanners]);

    // Effect to fetch price list when filters change
    React.useEffect(() => {
        // After filters are loaded for the first time, mark as no longer initial load
        if (isInitialLoad.current && !filtersLoading) {
            isInitialLoad.current = false;
            return; // Don't trigger price list fetch when filters first finish loading
        }

        // Check for 'Новинка' in LEVEL3 selection
        const level3Selection = selectedFilters.LEVEL3 || [];
        const hasNovinka = level3Selection.some(item => getOptionValue(item) === 'Новинка');
        setIsGrouped(!hasNovinka);

        if (user) {
            // Convert selectedFilters to the format expected by the API
            getPriceList({
                selectedFilters: selectedFilters,
                search: searchValue,
                page: pagePagination || 1,
                pageSize: pageSize,
                showOrderedProd: checkedOrdered
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, pagePagination, pageSize, selectedFilters, checkedOrdered, lang, filtersLoading]);

    // Handler for unified filter change
    const handleFilterChange = (filterKey) => (event, newValue) => {
        const newSelectedFilters = {
            ...selectedFilters,
            [filterKey]: newValue
        };
        setSelectedFilters(newSelectedFilters);
        setPagePagination(1);
    };

    const columns = [
        { id: 'QTARTCODE', label: l('Code'), minWidth: 50, type: CellType.STRING },
        { id: 'FNAME', label: l('Name'), minWidth: 150, type: CellType.PRODUCT_NAME },
        { id: 'FIMG', label: l('Image'), minWidth: 60, type: CellType.IMG, align: 'center' },
        { id: 'FINSTOCK', label: l('Availability'), minWidth: 100, type: CellType.FINSTOCK, align: 'center' },
        { id: 'FPRICE1', label: l(config.price1Label) + (config.appCompno === '2' ? `,${config.currencySignPrice1}` : ''), minWidth: 84, type: CellType.PRISE_WITH_DISCOUNT, align: 'center' },
        { id: 'FPRICE2', label: l(config.price2Label) + (config.appCompno === '2' ? `,${config.currencySignPrice2}` : ''), minWidth: 44, type: CellType.STRING_WITH_CURRENCY, align: 'center' },
        { id: 'QUANT', label: l('Quantity'), minWidth: 68, type: CellType.EDITABLE_NUMBER },
        { id: 'FMINQTY', label: l('Min. order'), minWidth: 56, type: CellType.MIN_QTY, align: 'center', headerTooltip: l('Min order quantity tooltip') },
        { id: 'FPACQTY', label: l('Pack'), minWidth: 44, type: CellType.PACK_QTY, align: 'center', headerTooltip: l('Quantity in a package') },
        { id: 'ITEMSUM', label: l('Sum'), minWidth: 68, type: CellType.PRODUCT_SUM },
    ];

    const debounceTimers = React.useRef({});

    const handelAction = (event, type, row) => {
        const { value } = event.target;

        switch (type) {
            case CellType.EDITABLE_NUMBER:
                const key = row.QTARTCODE;

                if (debounceTimers.current[key]) {
                    clearTimeout(debounceTimers.current[key]);
                }

                debounceTimers.current[key] = setTimeout(() => {
                    const effectiveQty = getEffectiveOrderQty(row);
                    let quant = parseInt(value, 10) || 0;

                    if (effectiveQty > 1 && quant % effectiveQty !== 0) {
                        quant = Math.max(effectiveQty, Math.round(quant / effectiveQty) * effectiveQty);
                    }

                    event.target.value = quant.toString();

                    row.QUANT = quant;
                    const discount = row.FDISCOUNT || 0;

                    let pricewithoutDiscount = Math.round(row.FPRICE1 * row.QUANT * 100) / 100;
                    let priceWithDiscount = Math.round((pricewithoutDiscount - (pricewithoutDiscount * discount / 100)) * 100) / 100;
                    row.ITEMSUM = priceWithDiscount;

                    row.SUMFWEIGHT = Math.round(row.FWEIGHT * row.QUANT * 1000) / 1000;
                    row.SUMFVOLUME = Math.round(row.FVOLUME * row.QUANT * 100000) / 100000;

                    const updatedTableData = priceList.map(item => {
                        if (item.QTARTCODE === row.QTARTCODE) {
                            return {
                                ...item,
                                QUANT: row.QUANT,
                                ITEMSUM: row.ITEMSUM
                            };
                        }
                        return item;
                    });

                    setPriceList(updatedTableData);

                    if (user) {
                        try {
                            const fetchData = async () => {
                                const basket = await basketService.updateBasket(row);
                                setBasketRecoil({ basket });
                                await getOrderedByCategoryAndSumByCustCode();
                            };

                            fetchData();
                        } catch (error) {
                            console.error('Error update Cart :>> ', error);
                        }
                    }

                    delete debounceTimers.current[key];
                }, 500);

                break;

            case CellType.PRODUCT_NAME:
                const url = compDetailUrl + row.FINFOPAGE;
                setOpenDetail(true);
                setDetailUrl(url);
                break;

            default:
                break;
        }
    };

    const hendleOrderProd = (e) => {
        const { checked } = e.target;

        if (checked) {
            if (user) {
                getPriceList({
                    selectedFilters: selectedFilters,
                    search: searchValue,
                    page: 1,
                    pageSize: pageSize,
                    showOrderedProd: true
                });
            }
            setPagePagination(1);
        }

        setCheckedOrdered(checked);
    };

    const debounceRef = React.useRef(null);
    const handelSearch = (value) => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        debounceRef.current = setTimeout(() => {
            if (user) {
                getPriceList({
                    selectedFilters: selectedFilters,
                    search: value,
                    page: 1,
                    pageSize: pageSize,
                    showOrderedProd: checkedOrdered
                });
            }
        }, 1000);

        setSearchValue(value);
        setPagePagination(1);
    };

    const handleChangeRowsPerPage = (event) => {
        setPageSize(+event.target.value);
    };

    const handleChangePage = (event, newPage) => {
        window.scrollTo(0, 0);
        setPagePagination(newPage);
    };

    // Handler for banner click
    const handleBannerClick = (banner) => {
        if (!banner || filtersLoading) {
            return;
        }

        // Map banner fields to filter keys
        const newFilters = {};

        // Helper function to find matching options from filter config
        const findMatchingOptions = (filterKey, values) => {
            const filter = filtersConfig.find(f => f.key === filterKey);
            if (!filter || !filter.options) {
                return [];
            }

            // Match values with existing options
            const matched = values.map(value => {
                // Якщо value - це об'єкт з мовами (для LEVEL1, LEVEL2, LEVEL3)
                let searchValue = value;
                if (typeof value === 'object' && (value.ua || value.en || value.ru)) {
                    // Беремо значення відповідно до поточної мови
                    const langKey = lang === 'ru' ? 'ru' : lang === 'en' ? 'en' : 'ua';
                    searchValue = value[langKey] || value.ua || value.en || value.ru;
                }

                const option = filter.options.find(opt => {
                    const optValue = getOptionValue(opt);
                    return optValue === searchValue;
                });
                return option || { value: searchValue, label: searchValue };
            });

            return matched;
        };

        // Mapping banner data to filter structure
        if (banner.categories && banner.categories.length > 0) {
            newFilters.LEVEL1 = findMatchingOptions('LEVEL1', banner.categories);
        }

        if (banner.brand && banner.brand.length > 0) {
            // LEVEL2 - бренд
            newFilters.LEVEL2 = findMatchingOptions('LEVEL2', banner.brand);
        }

        if (banner.fish_type && banner.fish_type.length > 0) {
            const fishTypeFilterKey = filtersConfig.find(f => f.key === 'fishes')?.key;
            
            if (fishTypeFilterKey) {
                newFilters[fishTypeFilterKey] = findMatchingOptions(fishTypeFilterKey, banner.fish_type);
            } else {
                console.warn('Fish type filter not found in filtersConfig');
            }
        }

        if (banner.fishing_type && banner.fishing_type.length > 0) {
            const fishingTypeFilterKey = filtersConfig.find(f => f.key === 'types')?.key;
            
            if (fishingTypeFilterKey) {
                newFilters[fishingTypeFilterKey] = findMatchingOptions(fishingTypeFilterKey, banner.fishing_type);
            } else {
                console.warn('Fishing type filter not found in filtersConfig');
            }
        }

        if (banner.other && banner.other.length > 0) {
            // LEVEL3 - інше
            newFilters.LEVEL3 = findMatchingOptions('LEVEL3', banner.other);
        }

        // Set filters
        setSelectedFilters(newFilters);

        // Set search string (if available)
        if (banner.search_strings && banner.search_strings.trim() !== '') {
            const searchString = banner.search_strings;
            setSearchValue(searchString);
            
            // Trigger immediate price list fetch with new filters and search
            if (user) {
                getPriceList({
                    selectedFilters: newFilters,
                    search: searchString,
                    page: 1,
                    pageSize: pageSize,
                    showOrderedProd: checkedOrdered
                });
            }
        } else {
            // Trigger price list fetch with just filters
            if (user) {
                getPriceList({
                    selectedFilters: newFilters,
                    search: searchValue,
                    page: 1,
                    pageSize: pageSize,
                    showOrderedProd: checkedOrdered
                });
            }
        }

        // Reset pagination
        setPagePagination(1);
        
    };

    // Render a single filter component
    const renderFilter = (filter) => {
        const options = getFilteredOptions(filter);
        const isDisabled = filter.key === 'LEVEL3' && options.length === 0;

        return (
            <Autocomplete
                key={filter.key}
                multiple
                id={filter.key}
                value={selectedFilters[filter.key] || []}
                onChange={handleFilterChange(filter.key)}
                options={options}
                getOptionLabel={(option) => getOptionLabel(option, filter)}
                isOptionEqualToValue={(option, value) =>
                    getOptionValue(option) === getOptionValue(value)
                }
                disabled={isDisabled}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        variant="standard"
                        label={getLocalizedLabel(filter.label)}
                        placeholder=""
                    />
                )}
            />
        );
    };

    // Перевірка чи є банери для показу
    const shouldShowBanners = () => {
        if (!activeBanners || activeBanners.length === 0) return false;
        
        // Фільтруємо банери які можна показувати
        const visibleBanners = activeBanners.filter(banner => {
            if (!banner.image_url) return false;
            
            // Якщо банер тестовий, показувати тільки для WEBTEST або argat.sergey@goldencatch.ua
            if (banner.is_test) {
                return user && (user.CODE === 'WEBTEST' || user.EMAIL === 'argat.sergey@goldencatch.ua');
            }
            
            // Якщо банер не тестовий, показувати всім
            return true;
        });
        
        return visibleBanners.length > 0;
    };
    
    // Отримати банери які можна показати
    const getVisibleBanners = () => {
        if (!activeBanners || activeBanners.length === 0) return [];
        
        return activeBanners.filter(banner => {
            if (!banner.image_url) return false;
            
            // Якщо банер тестовий, показувати тільки для WEBTEST або argat.sergey@goldencatch.ua
            if (banner.is_test) {
                return user && (user.CODE === 'WEBTEST' || user.EMAIL === 'argat.sergey@goldencatch.ua');
            }
            
            // Якщо банер не тестовий, показувати всім
            return true;
        });
    };

    return (
        <>
            {shouldShowBanners() && (
                <Box display="none" sx={{ display: { md: 'flex', lg: 'none' } }} alignItems="center" flexGrow={1} justifyContent={'center'} mb={2}>
                    <BannerCarousel
                        banners={getVisibleBanners()}
                        language={lang}
                        onBannerClick={handleBannerClick}
                        loading={filtersLoading}
                        variant="mobile"
                    />
                </Box>
            )}
            <Box display="flex" justifyContent="space-between" margin={'10px 0'} flexWrap={'wrap-reverse'}>
                <Box display="flex" flexWrap={'wrap'} gap={1}>
                    
                    <Box display={'flex'} alignItems={'center'} sx={{ width: { xs: 'auto', md: '300px' } }}>
                        <Typography component="h2" variant="h5">
                            {l('Price list')}
                        </Typography>
                    </Box>
                    <Box display={'flex'} gap={1} flexWrap={'wrap'}></Box>
                   
                    <Box display="flex" flexDirection="column" justifyContent="center">
                        <Box display='flex' alignItems={'center'}>
                            {l('Order amount')}: {totals.totSum}{config.currencySignPrice1}
                            {config.appCompno === '1' ? (
                                <Tooltip title={l('Exchange rate') + `: ${totals.FRRATE} ₴`}>
                                    <Typography marginLeft={'4px'}>
                                        {`(~${Math.round(totals.FRRATE * totals.totSum)}₴)`}
                                    </Typography>
                                </Tooltip>
                            ) : ''}
                        </Box>
                        <Box display="flex">
                            <Typography color="#dc3545" marginRight={1} component="p" variant="p">
                                {totals.totWeight} {l('Kg')}
                            </Typography>
                            <Typography color="#007bff" component="p" variant="p">
                                {totals.totVolume} {l('m³')}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                <Box display="flex" alignItems="center" gap={2} flexWrap={'wrap'} flexGrow={1} sx={{justifyContent: {xs: 'space-between', md: 'flex-end'}}}>
                    {shouldShowBanners() && (
                        <Box sx={{display: {xs:'none', md:'none', lg: 'flex'}}} alignItems="center" flexGrow={1} justifyContent={'center'} marginRight={2}>
                            <BannerCarousel
                                banners={getVisibleBanners()}
                                language={lang}
                                onBannerClick={handleBannerClick}
                                loading={filtersLoading}
                                variant="desktop"
                            />
                        </Box>
                    )}

                    <Search onChange={handelSearch} value={searchValue} />
                </Box>
            </Box>
            <Box display="flex">
                <Box className="shadow" sx={{
                    backgroundColor: 'white',
                    display: showFilters ? 'block' : 'none',
                    flex: '0 0 288px',
                }}
                    minWidth="288px"
                    padding={1}
                    marginRight={1}
                    borderRadius="4px">
                    <Box onClick={toggleFilters} sx={{ cursor: 'pointer' }} display="flex" alignItems="center">
                        <FilterAltIcon />
                        {l('Filtration')}
                    </Box>

                    {/* Show skeletons while filters are loading */}
                    {filtersLoading ? (
                        <>
                            {[1, 2, 3, 4, 5].map((item) => (
                                <Box key={item}>
                                    <Skeleton variant="text" width="100%" height={50} />
                                </Box>
                            ))}
                        </>
                    ) : (
                        <>
                            {/* Render all filters dynamically */}
                            {filtersConfig.map(filter => renderFilter(filter))}
                        </>
                    )}

                    <FormGroup>
                        <FormControlLabel
                            control={<Checkbox onChange={hendleOrderProd} checked={checkedOrdered} />}
                            label={l('Show ordered products')}
                        />
                    </FormGroup>
                    <Divider orientation="horizontal" />
                    <Typography marginTop={1} fontWeight="bold">{l('Ordered by categories')}</Typography>
                    {orderCategories.map((item, index) => (
                        <Box key={index} display="flex" justifyContent="space-between">
                            <Typography>{item.LEVEL1}</Typography>
                            <Typography>{item.total}</Typography>
                        </Box>
                    ))}
                </Box>

                <Box width="100%" minWidth={0}>
                    <TableData
                        columns={columns}
                        rows={priceList}
                        cellBtnActionByType={handelAction}
                        isPriceList={true}
                        ownPagination={ownPagination}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        isGrouped={isGrouped}
                        tableEmptyText={priceListLoading ? l('Loading...') : l('No data available')}
                        openFilters={toggleFilters}
                        filtersOpen={showFilters}
                    />
                </Box>
            </Box>
            <DetailScreen isOpened={openDetail} setOpenDetail={setOpenDetail} detailUrl={detailUrl} />
        </>
    );
}

export default PriceList;
